import {
  ChartType,
  type BaseDataPoint,
  type ChartClickEventArgs,
  type ChartDataset,
} from '@powerportalspro/react-charts';
import { FluentUIChart } from '@powerportalspro/react-fluent';
import { useState } from 'react';

// Sales DataPoint variant that carries Region / OrderCount alongside the
// numeric value. Mirrors Blazor's `SalesDataPoint : DataPoint` pattern —
// extend `BaseDataPoint` for the value/color fields and add whatever
// extra context the click handler should be able to recover.
//
// The dataset / chart get parameterized with this type below
// (`ChartDataset<SalesDataPoint>`, `<FluentUIChart<SalesDataPoint> …>`),
// so the click handler's `args.dataPoint` is typed automatically —
// no `as unknown as …` casts required at either the construction or
// recovery side.
interface SalesDataPoint extends BaseDataPoint {
  region: string;
  orderCount: number;
}

const LABELS = ['January', 'February', 'March', 'April', 'May', 'June', 'July'];

// The Sales dataset uses the SalesDataPoint variant; the other two
// datasets use the default `BaseDataPoint` since they have no extra
// context to surface. They coexist in the same `ChartDataset<SalesDataPoint>[]`
// array because every BaseDataPoint shape is structurally compatible
// with a richer SalesDataPoint when its extra fields are absent.
const DATASETS: ChartDataset<SalesDataPoint>[] = [
  {
    label: 'Sales',
    data: [
      { value: 65, region: 'North', orderCount: 12 },
      { value: 59, region: 'North', orderCount: 10 },
      { value: 80, region: 'South', orderCount: 18 },
      { value: 81, region: 'South', orderCount: 19 },
      { value: 56, region: 'East', orderCount: 9 },
      { value: 55, region: 'East', orderCount: 9 },
      { value: 40, region: 'West', orderCount: 7 },
    ],
  },
  {
    label: 'Returns',
    data: [12, 19, 8, 15, 6, 9, 4].map((v) => ({ value: v, region: '', orderCount: 0 })),
  },
  {
    label: 'Refunds',
    data: [3, 5, 2, 6, 1, 4, 2].map((v) => ({ value: v, region: '', orderCount: 0 })),
  },
];

export function BarChartMainSample() {
  const [lastClick, setLastClick] = useState<ChartClickEventArgs<SalesDataPoint> | null>(null);
  // No cast — `lastClick.dataPoint` is typed `SalesDataPoint | undefined`
  // because we parameterized the FluentUIChart's generic below.
  const sales = lastClick?.dataPoint;
  return (
    <>
      <FluentUIChart<SalesDataPoint>
        type={ChartType.Bar}
        labels={LABELS}
        datasets={DATASETS}
        onElementClick={setLastClick}
      />
      {lastClick && (
        <p style={{ marginTop: '12px' }}>
          <strong>Last clicked:</strong> {lastClick.datasetLabel} — {lastClick.label} = {lastClick.value}
          {sales?.region && (
            <span>
              {' '}&nbsp;<em>(region: {sales.region}, orders: {sales.orderCount})</em>
            </span>
          )}
        </p>
      )}
    </>
  );
}
